import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useMeInfo, useIsAdmin } from "../hooks/useMeInfo";
import { Badge, Spinner } from "../components/UI";
import dayjs from "dayjs";

// Vereint die frühere "Beschlussverfolgung" (Liste, Filter, Statuswechsel)
// und "Beschlusskontrolle" (Fristen-Dashboard, Fortschritt) zu EINER Seite -
// beide zeigten dieselben Beschluss-Daten, nur unterschiedlich aufbereitet,
// was in der Sidebar unnötig doppelt wirkte (Nutzer-Feedback).
const STATUS_LIST = ["alle", "offen", "in_bearbeitung", "erledigt", "abgelehnt"];

export default function Beschluesse() {
  const { info: meInfo } = useMeInfo();
  const isAdmin = useIsAdmin();
  const myEmail = meInfo?.email || null;
  const [beschluesse, setBeschluesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("alle");

  const load = () => api.getBeschluesse().then(setBeschluesse).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const updateStatus = async (b, status) => {
    await api.updateBeschluss(b.id, { status });
    load();
  };

  const updateFortschritt = async (b, wert) => {
    await api.updateBeschlussFortschritt(b.id, wert);
    load();
  };

  if (loading) return <Spinner />;

  const jetzt = dayjs();
  const istUeberfaellig = b => b.faellig_am && dayjs(b.faellig_am).isBefore(jetzt) && b.status !== "erledigt";
  const istDemnaechstFaellig = b => b.faellig_am && !istUeberfaellig(b) && dayjs(b.faellig_am).isBefore(jetzt.add(7, "day")) && b.status !== "erledigt";

  const stats = {
    ueberfaellig: beschluesse.filter(istUeberfaellig).length,
    in_bearbeitung: beschluesse.filter(b => b.status === "in_bearbeitung").length,
    demnaechst_faellig: beschluesse.filter(istDemnaechstFaellig).length,
    erledigt: beschluesse.filter(b => b.status === "erledigt").length,
  };

  const filtered = filter === "alle" ? beschluesse : beschluesse.filter(b => b.status === filter);

  return (
    <div>
      <div className="page-header">
        <div><div className="breadcrumb">Beschlüsse</div><h2>Beschlussverfolgung</h2></div>
      </div>

      <div className="content">
        <div className="stats-grid">
          <div className="stat-card" style={{ borderLeftColor: "var(--danger)" }}>
            <div className="stat-value" style={{ color: "var(--danger)" }}>{stats.ueberfaellig}</div>
            <div className="stat-label">Überfällig</div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: "var(--info)" }}>
            <div className="stat-value" style={{ color: "var(--info)" }}>{stats.in_bearbeitung}</div>
            <div className="stat-label">In Bearbeitung</div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: "var(--warn)" }}>
            <div className="stat-value" style={{ color: "var(--warn)" }}>{stats.demnaechst_faellig}</div>
            <div className="stat-label">Demnächst fällig (7 Tage)</div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: "var(--ok)" }}>
            <div className="stat-value" style={{ color: "var(--ok)" }}>{stats.erledigt}</div>
            <div className="stat-label">Erledigt</div>
          </div>
        </div>

        <div className="flex gap-8 mb-16">
          {STATUS_LIST.map(s => (
            <button key={s} className={`btn btn-sm ${filter === s ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setFilter(s)}>
              {s === "alle" ? "Alle" : s === "in_bearbeitung" ? "In Bearbeitung" :
               s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        <div className="card">
          {filtered.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">✓</div><p>Keine Beschlüsse in diesem Filter</p></div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Beschluss</th>
                  <th>Verantwortlich</th>
                  <th>Fällig</th>
                  <th>Fortschritt</th>
                  <th>Status</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => {
                  const ueberfaellig = istUeberfaellig(b);
                  const demnaechst = istDemnaechstFaellig(b);
                  const darfBearbeiten = isAdmin || (b.beauftragter_email && b.beauftragter_email.toLowerCase() === (myEmail || "").toLowerCase());
                  return (
                    <tr key={b.id} style={{ background: ueberfaellig ? "var(--danger-bg)" : undefined }}>
                      <td style={{ color: "var(--ink-faint)", fontFamily: "var(--mono)" }}>{b.id}</td>
                      <td>
                        <div>{b.text}</div>
                        {b.notizen && <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 2 }}>{b.notizen}</div>}
                      </td>
                      <td>{b.verantwortlich || "—"}</td>
                      <td>
                        {b.faellig_am ? (
                          <span style={{ fontFamily: "var(--mono)", fontSize: 12, color: ueberfaellig ? "var(--danger)" : demnaechst ? "var(--warn)" : "inherit", fontWeight: (ueberfaellig || demnaechst) ? 600 : 400 }}>
                            {ueberfaellig && "⚠ "}{dayjs(b.faellig_am).format("DD.MM.YYYY")}
                          </span>
                        ) : "—"}
                      </td>
                      <td style={{ minWidth: 110 }}>
                        {darfBearbeiten ? (
                          <input type="range" min="0" max="100" step="10" value={b.fortschritt_prozent || 0}
                            onChange={e => updateFortschritt(b, parseInt(e.target.value, 10))}
                            style={{ width: 80, verticalAlign: "middle" }} />
                        ) : null}
                        <span style={{ fontSize: 11, color: "var(--ink-faint)", marginLeft: 6 }}>{b.fortschritt_prozent || 0}%</span>
                      </td>
                      <td><Badge status={b.status} /></td>
                      <td>
                        {darfBearbeiten ? (
                          <select className="form-control" style={{ width: "auto", padding: "3px 6px", fontSize: 12 }}
                            value={b.status} onChange={e => updateStatus(b, e.target.value)}>
                            <option value="offen">Offen</option>
                            <option value="in_bearbeitung">In Bearbeitung</option>
                            <option value="erledigt">Erledigt</option>
                            <option value="abgelehnt">Abgelehnt</option>
                          </select>
                        ) : (
                          <span style={{ color: "var(--ink-faint)", fontSize: 12 }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
